import React, { useEffect, useState } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import { toast } from "react-hot-toast";
import {
    Backdrop,
    Box,
    Button,
    Chip,
    CircularProgress,
    Fade,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Modal,
    Paper,
    Select,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    FiClock,
    FiRefreshCw,
    FiSave,
    FiX,
    FiTrendingUp,
    FiTrendingDown,
    FiChevronLeft,
    FiChevronRight,
    FiPlus,
    FiSettings,
} from "react-icons/fi";

const TenantWallet = () => {
    const [tenants, setTenants] = useState([]);
    const [wallets, setWallets] = useState({});
    const [loading, setLoading] = useState(true);
    const [inputValues, setInputValues] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [selectedTenant, setSelectedTenant] = useState(null);
    const [activeTab, setActiveTab] = useState("credit"); // 'credit' or 'debit'
    const [historyData, setHistoryData] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [savingTenantId, setSavingTenantId] = useState(null);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 10,
        hasNextPage: false,
        hasPreviousPage: false
    });

    // Config modal state
    const [showConfigModal, setShowConfigModal] = useState(false);
    const [selectedConfigTenant, setSelectedConfigTenant] = useState(null);
    const [companySpocEmail, setCompanySpocEmail] = useState("");
    const [csmEmail, setCsmEmail] = useState("");
    const [accountsEmail, setAccountsEmail] = useState("");
    const [thresholdBalance, setThresholdBalance] = useState("");
    const [configSaving, setConfigSaving] = useState(false);

    // Fetch tenants and wallet data
    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        toast.remove();
        const toastId = toast.loading("Fetching tenant wallet data...");

        try {
            // Fetch tenants
            const tenantsResponse = await axios({
                method: "get",
                url: `${process.env.REACT_APP_BASE_URL}/admin/company`,
                headers: {
                    Authorization: "Bearer " + sessionStorage.getItem("access_token"),
                },
            });

            // Fetch wallets
            const walletsResponse = await axios({
                method: "get",
                url: `${process.env.REACT_APP_BASE_URL}/admin/tenant-wallet`,
                headers: {
                    Authorization: "Bearer " + sessionStorage.getItem("access_token"),
                },
            });

            if (tenantsResponse.data.success) {
                setTenants(tenantsResponse.data.body);

                // Create wallet map by tenant_id
                const walletMap = {};
                const initialInputs = {};

                if (walletsResponse.data.success && walletsResponse.data.body) {
                    walletsResponse.data.body.forEach((wallet) => {
                        walletMap[wallet.tenant_id] = wallet;
                        initialInputs[wallet.tenant_id] = "";
                    });
                }

                // Set initial input values for all tenants
                tenantsResponse.data.body.forEach((tenant) => {
                    if (!initialInputs[tenant.tenant_id]) {
                        initialInputs[tenant.tenant_id] = "";
                    }
                });

                setWallets(walletMap);
                setInputValues(initialInputs);
                toast.success("Data fetched successfully", { id: toastId });
            }
        } catch (error) {
            console.error(error);
            toast.error(
                error.response?.data?.message || error.message || "Failed to fetch data",
                { id: toastId }
            );
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (tenantId, value) => {
        setInputValues((prev) => ({
            ...prev,
            [tenantId]: value,
        }));
    };

    const handleSaveWallet = async (tenant) => {
        const amount = Number(inputValues[tenant.tenant_id]);
        if (!amount || Number.isNaN(amount) || amount <= 0) {
            toast.error("Enter a valid credit amount");
            return;
        }

        setSavingTenantId(tenant.tenant_id);
        const toastId = toast.loading("Crediting wallet...");

        try {
            const response = await axios({
                method: "post",
                url: `${process.env.REACT_APP_BASE_URL}/admin/tenant-wallet`,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + sessionStorage.getItem("access_token"),
                    slug: tenant.slug || tenant.tenant_id || "",
                },
                data: {
                    tenant_id: tenant.tenant_id,
                    tenant_name: tenant.name,
                    wallet_balance: amount,
                },
            });

            if (response.data.success) {
                toast.success("Wallet credited successfully", { id: toastId });

                // Update local wallet state
                setWallets((prev) => ({
                    ...prev,
                    [tenant.tenant_id]: response.data.body,
                }));
                setInputValues((prev) => ({
                    ...prev,
                    [tenant.tenant_id]: "",
                }));
            } else {
                toast.error(response.data.message || "Failed to save", { id: toastId });
            }
        } catch (error) {
            console.error(error);
            toast.error(
                error.response?.data?.message || error.message || "Failed to save",
                { id: toastId }
            );
        } finally {
            setSavingTenantId(null);
        }
    };

    const handleOpenModal = (tenant) => {
        setSelectedTenant(tenant);
        setShowModal(true);
        setActiveTab("credit");
        setPagination({
            currentPage: 1,
            totalPages: 1,
            totalCount: 0,
            limit: 10,
            hasNextPage: false,
            hasPreviousPage: false
        });
        fetchHistory(tenant.tenant_id, "credit", 1, tenant);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedTenant(null);
        setHistoryData([]);
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setPagination({
            currentPage: 1,
            totalPages: 1,
            totalCount: 0,
            limit: 10,
            hasNextPage: false,
            hasPreviousPage: false
        });
        fetchHistory(selectedTenant.tenant_id, tab, 1, selectedTenant);
    };

    const fetchHistory = async (tenantId, transactionType, page, tenantObj = selectedTenant) => {
        setHistoryLoading(true);
        try {
            const response = await axios({
                method: "get",
                url: `${process.env.REACT_APP_BASE_URL}/admin/tenant-wallet/${tenantId}/passbook`,
                headers: {
                    Authorization: "Bearer " + sessionStorage.getItem("access_token"),
                    slug: tenantObj?.slug || tenantObj?.tenant_id || tenantId || "",
                },
                params: {
                    transaction_type: transactionType,
                    page: page,
                    limit: 10
                }
            });

            if (response.data.success) {
                setHistoryData(response.data.body.data || []);
                setPagination(response.data.body.pagination || pagination);
            } else {
                toast.error(response.data.message || "Failed to fetch history");
            }
        } catch (error) {
            console.error(error);
            toast.error(
                error.response?.data?.message || error.message || "Failed to fetch history"
            );
        } finally {
            setHistoryLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        fetchHistory(selectedTenant.tenant_id, activeTab, newPage, selectedTenant);
    };

    const handleOpenConfigModal = (tenant) => {
        if (!tenant) return;
        setSelectedConfigTenant(tenant);
        const wallet = wallets[tenant.tenant_id];
        setCompanySpocEmail(wallet?.spoc_emails?.[0] || "");
        setCsmEmail(wallet?.spoc_emails?.[1] || "");
        setAccountsEmail(wallet?.spoc_emails?.[2] || "");
        setThresholdBalance(
            wallet?.threshold_balance !== undefined && wallet?.threshold_balance !== null
                ? wallet.threshold_balance
                : ""
        );
        setShowConfigModal(true);
    };

    const handleCloseConfigModal = () => {
        setShowConfigModal(false);
        setSelectedConfigTenant(null);
        setCompanySpocEmail("");
        setCsmEmail("");
        setAccountsEmail("");
        setThresholdBalance("");
    };

    const handleSaveConfig = async () => {
        if (!selectedConfigTenant) {
            toast.error("Tenant not selected");
            return;
        }

        setConfigSaving(true);
        const toastId = toast.loading("Saving wallet details...");

        try {
            const response = await axios({
                method: "post",
                url: `${process.env.REACT_APP_BASE_URL}/admin/tenant-wallet/config`,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + sessionStorage.getItem("access_token"),
                    slug: selectedConfigTenant.slug || selectedConfigTenant.tenant_id || "",
                },
                data: {
                    tenant_id: Number(selectedConfigTenant.tenant_id),
                    tenant_name: selectedConfigTenant.name,
                    company_spoc_email: companySpocEmail.trim(),
                    csm_email: csmEmail.trim(),
                    accounts_email: accountsEmail.trim(),
                    threshold_balance: thresholdBalance !== "" ? Number(thresholdBalance) : null,
                },
            });

            if (response.data.success) {
                toast.success("Wallet details saved successfully", { id: toastId });
                setWallets((prev) => ({
                    ...prev,
                    [selectedConfigTenant.tenant_id]: response.data.body,
                }));
                handleCloseConfigModal();
            } else {
                toast.error(response.data.message || "Failed to save details", { id: toastId });
            }
        } catch (error) {
            console.error(error);
            toast.error(
                error.response?.data?.message || error.message || "Failed to save details",
                { id: toastId }
            );
        } finally {
            setConfigSaving(false);
        }
    };

    const columns = [
        {
            name: "S.No",
            selector: (row, idx) => idx + 1,
            sortable: true,
            width: "70px",
        },
        {
            name: "Tenant ID",
            selector: (row) => row.tenant_id,
            sortable: true,
            width: "100px",
        },
        {
            name: "Client Name",
            selector: (row) => row.name,
            sortable: true,
            minWidth: "180px",
        },
        {
            name: "Current Balance",
            cell: (row) => {
                const wallet = wallets[row.tenant_id];
                return wallet ? `₹ ${Number(wallet.wallet_balance).toFixed(2)}` : "₹ 0.00";
            },
            width: "140px",
        },
        {
            name: "Threshold Balance",
            cell: (row) => {
                const wallet = wallets[row.tenant_id];
                if (wallet?.threshold_balance !== undefined && wallet?.threshold_balance !== null) {
                    return `₹ ${Number(wallet.threshold_balance).toLocaleString("en-IN")}`;
                }
                return "-";
            },
            width: "150px",
        },
        {
            name: "SPOC Emails",
            cell: (row) => {
                const wallet = wallets[row.tenant_id];
                const spoc = wallet?.spoc_emails?.[0];
                const csm = wallet?.spoc_emails?.[1];
                const acc = wallet?.spoc_emails?.[2];
                const hasAny = spoc || csm || acc;

                if (!hasAny) return "-";

                const tooltipContent = (
                    <Box sx={{ p: 0.5 }}>
                        <Typography variant="caption" display="block"><strong>SPOC:</strong> {spoc || "N/A"}</Typography>
                        <Typography variant="caption" display="block"><strong>CSM:</strong> {csm || "N/A"}</Typography>
                        <Typography variant="caption" display="block"><strong>Accounts:</strong> {acc || "N/A"}</Typography>
                    </Box>
                );

                return (
                    <Tooltip title={tooltipContent} arrow placement="top">
                        <Chip
                            size="small"
                            label={`${[spoc, csm, acc].filter(Boolean).length} Contact(s)`}
                            sx={{
                                fontWeight: 700,
                                bgcolor: "rgba(99, 102, 241, 0.1)",
                                color: "rgb(79, 70, 229)",
                                border: "1px solid rgba(99, 102, 241, 0.2)",
                            }}
                        />
                    </Tooltip>
                );
            },
            width: "140px",
        },
        {
            name: "Credit Amount",
            cell: (row) => (
                <TextField
                    type="number"
                    size="small"
                    value={inputValues[row.tenant_id] ?? ""}
                    onChange={(e) => handleInputChange(row.tenant_id, e.target.value)}
                    placeholder="0.00"
                    inputProps={{ step: "0.01", min: 0 }}
                    sx={{ width: 120 }}
                />
            ),
            width: "150px",
        },
        {
            name: "Last Updated",
            cell: (row) => {
                const wallet = wallets[row.tenant_id];
                return wallet?.updated_at ? new Date(wallet.updated_at).toLocaleString() : "-";
            },
            width: "180px",
        },
        {
            name: "Updated By",
            cell: (row) => {
                const wallet = wallets[row.tenant_id];
                return wallet?.updated_by || "-";
            },
            width: "130px",
        },
        {
            name: "Config",
            cell: (row) => (
                <Tooltip title="Configure SPOC & Threshold">
                    <IconButton
                        onClick={() => handleOpenConfigModal(row)}
                        size="small"
                        sx={{
                            border: "1px solid",
                            borderColor: "rgba(99, 102, 241, 0.4)",
                            color: "rgb(79, 70, 229)",
                            transition: "transform 160ms ease, background-color 160ms ease",
                            "&:hover": {
                                transform: "translateY(-1px)",
                                backgroundColor: "rgba(99, 102, 241, 0.08)",
                            },
                        }}
                    >
                        <FiSettings />
                    </IconButton>
                </Tooltip>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: "80px",
        },
        {
            name: "History",
            cell: (row) => (
                <Tooltip title="View passbook">
                    <IconButton
                        onClick={() => handleOpenModal(row)}
                        size="small"
                        sx={{
                            border: "1px solid",
                            borderColor: "rgba(2, 132, 199, 0.4)",
                            color: "rgb(2, 132, 199)",
                            transition: "transform 160ms ease, background-color 160ms ease",
                            "&:hover": {
                                transform: "translateY(-1px)",
                                backgroundColor: "rgba(2, 132, 199, 0.08)",
                            },
                        }}
                    >
                        <FiClock />
                    </IconButton>
                </Tooltip>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: "80px",
        },
        {
            name: "Action",
            cell: (row) => (
                <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleSaveWallet(row)}
                    disabled={
                        savingTenantId === row.tenant_id ||
                        !Number(inputValues[row.tenant_id]) ||
                        Number(inputValues[row.tenant_id]) <= 0
                    }
                    startIcon={
                        savingTenantId === row.tenant_id ? (
                            <CircularProgress size={14} color="inherit" />
                        ) : (
                            <FiSave />
                        )
                    }
                    sx={{
                        textTransform: "none",
                        borderRadius: 2,
                        boxShadow: "none",
                        transition: "transform 160ms ease, box-shadow 160ms ease",
                        "&:hover": {
                            transform: "translateY(-1px)",
                            boxShadow: "0 10px 25px rgba(2, 6, 23, 0.12)",
                        },
                    }}
                >
                    Credit
                </Button>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: "110px",
        },
    ];

    const customStyles = {
        headCells: {
            style: {
                fontWeight: 700,
                fontSize: "12px",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: "rgba(15, 23, 42, 0.7)",
            },
        },
        rows: {
            style: {
                minHeight: "62px",
            },
        },
    };

    return (
        <div className="page-content">
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2, md: 3 },
                    borderRadius: 3,
                    border: "1px solid rgba(15, 23, 42, 0.08)",
                    background:
                        "radial-gradient(1200px circle at 0% 0%, rgba(56, 189, 248, 0.18), transparent 45%), radial-gradient(900px circle at 100% 0%, rgba(99, 102, 241, 0.16), transparent 45%), #fff",
                }}
            >
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    alignItems={{ xs: "flex-start", md: "center" }}
                    justifyContent="space-between"
                    sx={{ mb: 2 }}
                >
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                            Tenant Wallet
                        </Typography>
                        <Typography variant="body2" sx={{ color: "rgba(15, 23, 42, 0.65)", mt: 0.5 }}>
                            Credit wallets and review passbook entries.
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                            label={loading ? "Loading" : `${tenants.length} tenants`}
                            size="small"
                            sx={{
                                fontWeight: 700,
                                bgcolor: "rgba(2, 132, 199, 0.08)",
                                color: "rgb(2, 132, 199)",
                            }}
                        />
                        <Button
                            variant="outlined"
                            onClick={fetchData}
                            startIcon={<FiRefreshCw />}
                            sx={{
                                textTransform: "none",
                                borderRadius: 2,
                                transition: "transform 160ms ease",
                                "&:hover": { transform: "translateY(-1px)" },
                            }}
                        >
                            Refresh
                        </Button>
                    </Stack>
                </Stack>

                <Box
                    sx={{
                        borderRadius: 3,
                        overflow: "hidden",
                        border: "1px solid rgba(15, 23, 42, 0.08)",
                        backgroundColor: "rgba(255,255,255,0.75)",
                        backdropFilter: "blur(10px)",
                    }}
                >
                    <DataTable
                        title=""
                        data={tenants}
                        columns={columns}
                        striped
                        highlightOnHover
                        pointerOnHover
                        pagination
                        progressPending={loading}
                        customStyles={customStyles}
                    />
                </Box>
            </Paper>

            {/* Passbook History Modal */}
            <Modal
                open={showModal}
                onClose={handleCloseModal}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={showModal}>
                    <Box
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: "80%",
                            maxWidth: "900px",
                            maxHeight: "90vh",
                            bgcolor: "rgba(255, 255, 255, 0.9)",
                            backdropFilter: "blur(12px)",
                            boxShadow: "0 30px 80px rgba(2, 6, 23, 0.35)",
                            borderRadius: 2,
                            p: 3,
                            overflowY: "auto",
                        }}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ mb: 2 }}
                        >
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                    Passbook
                                </Typography>
                                <Typography variant="body2" sx={{ color: "rgba(15, 23, 42, 0.65)", mt: 0.25 }}>
                                    {selectedTenant?.name || ""}
                                </Typography>
                            </Box>
                            <Tooltip title="Close">
                                <IconButton
                                    onClick={handleCloseModal}
                                    sx={{
                                        border: "1px solid rgba(15, 23, 42, 0.12)",
                                        transition: "transform 160ms ease, background-color 160ms ease",
                                        "&:hover": {
                                            transform: "rotate(90deg)",
                                            backgroundColor: "rgba(15, 23, 42, 0.06)",
                                        },
                                    }}
                                >
                                    <FiX />
                                </IconButton>
                            </Tooltip>
                        </Stack>

                        {/* Tabs */}
                        <Tabs
                            value={activeTab}
                            onChange={(_, v) => handleTabChange(v)}
                            sx={{
                                mb: 2,
                                "& .MuiTab-root": { textTransform: "none", fontWeight: 800 },
                            }}
                        >
                            <Tab value="credit" icon={<FiTrendingUp />} iconPosition="start" label="Credits" />
                            <Tab value="debit" icon={<FiTrendingDown />} iconPosition="start" label="Debits" />
                        </Tabs>

                        {/* History Table */}
                        {historyLoading ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    py: 4,
                                }}
                            >
                                <CircularProgress size={28} />
                            </Box>
                        ) : historyData.length === 0 ? (
                            <Box sx={{ textAlign: "center", py: 4 }}>
                                <Typography variant="body2" sx={{ color: "rgba(15, 23, 42, 0.6)" }}>
                                    No {activeTab} history found
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    <table className="table table-striped table-hover">
                                        <thead>
                                            <tr>
                                                <th>Date</th>
                                                <th>Amount</th>
                                                <th>Balance Before</th>
                                                <th>Balance After</th>
                                                <th>Reference Type</th>
                                                <th>Reference ID</th>
                                                <th>Description</th>
                                                <th>Created By</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {historyData.map((entry, idx) => (
                                                <tr key={idx}>
                                                    <td>{new Date(entry.created_at).toLocaleString()}</td>
                                                    <td>
                                                        <Chip
                                                            size="small"
                                                            label={`₹ ${Number(entry.amount).toFixed(2)}`}
                                                            sx={{
                                                                fontWeight: 800,
                                                                bgcolor:
                                                                    entry.transaction_type === "credit"
                                                                        ? "rgba(34, 197, 94, 0.12)"
                                                                        : "rgba(239, 68, 68, 0.12)",
                                                                color:
                                                                    entry.transaction_type === "credit"
                                                                        ? "rgb(22, 163, 74)"
                                                                        : "rgb(220, 38, 38)",
                                                            }}
                                                        />
                                                    </td>
                                                    <td>₹ {Number(entry.balance_before).toFixed(2)}</td>
                                                    <td>₹ {Number(entry.balance_after).toFixed(2)}</td>
                                                    <td>
                                                        <Chip
                                                            size="small"
                                                            label={entry.reference_type || "-"}
                                                            sx={{
                                                                bgcolor: "rgba(15, 23, 42, 0.08)",
                                                                fontWeight: 700,
                                                            }}
                                                        />
                                                    </td>
                                                    <td>{entry.reference_id || "-"}</td>
                                                    <td>{entry.description || "-"}</td>
                                                    <td>{entry.created_by || "-"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {pagination.totalPages > 1 && (
                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <div>
                                            <span className="text-muted">
                                                Showing {((pagination.currentPage - 1) * pagination.limit) + 1} to{" "}
                                                {Math.min(
                                                    pagination.currentPage * pagination.limit,
                                                    pagination.totalCount
                                                )}{" "}
                                                of {pagination.totalCount} entries
                                            </span>
                                        </div>
                                        <nav>
                                            <ul className="pagination mb-0">
                                                <li
                                                    className={`page-item ${
                                                        !pagination.hasPreviousPage ? "disabled" : ""
                                                    }`}
                                                >
                                                    <button
                                                        className="page-link"
                                                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                                                        disabled={!pagination.hasPreviousPage}
                                                    >
                                                        <span
                                                            className="d-inline-flex align-items-center"
                                                            style={{ gap: 6 }}
                                                        >
                                                            <FiChevronLeft />
                                                            Previous
                                                        </span>
                                                    </button>
                                                </li>
                                                {[...Array(pagination.totalPages)].map((_, idx) => {
                                                    const page = idx + 1;
                                                    if (
                                                        page === 1 ||
                                                        page === pagination.totalPages ||
                                                        (page >= pagination.currentPage - 1 &&
                                                            page <= pagination.currentPage + 1)
                                                    ) {
                                                        return (
                                                            <li
                                                                key={page}
                                                                className={`page-item ${
                                                                    pagination.currentPage === page ? "active" : ""
                                                                }`}
                                                            >
                                                                <button
                                                                    className="page-link"
                                                                    onClick={() => handlePageChange(page)}
                                                                >
                                                                    {page}
                                                                </button>
                                                            </li>
                                                        );
                                                    } else if (
                                                        page === pagination.currentPage - 2 ||
                                                        page === pagination.currentPage + 2
                                                    ) {
                                                        return (
                                                            <li key={page} className="page-item disabled">
                                                                <span className="page-link">...</span>
                                                            </li>
                                                        );
                                                    }
                                                    return null;
                                                })}
                                                <li
                                                    className={`page-item ${!pagination.hasNextPage ? "disabled" : ""}`}
                                                >
                                                    <button
                                                        className="page-link"
                                                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                                                        disabled={!pagination.hasNextPage}
                                                    >
                                                        <span
                                                            className="d-inline-flex align-items-center"
                                                            style={{ gap: 6 }}
                                                        >
                                                            Next
                                                            <FiChevronRight />
                                                        </span>
                                                    </button>
                                                </li>
                                            </ul>
                                        </nav>
                                    </div>
                                )}
                            </>
                        )}
                    </Box>
                </Fade>
            </Modal>

            {/* Configure Tenant Wallet Modal */}
            <Modal
                open={showConfigModal}
                onClose={handleCloseConfigModal}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={showConfigModal}>
                    <Box
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: "90%",
                            maxWidth: "550px",
                            bgcolor: "rgba(255, 255, 255, 0.95)",
                            backdropFilter: "blur(16px)",
                            boxShadow: "0 30px 80px rgba(2, 6, 23, 0.35)",
                            borderRadius: 3,
                            p: 3.5,
                            border: "1px solid rgba(255, 255, 255, 0.8)",
                        }}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ mb: 3 }}
                        >
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                    Wallet Configuration
                                </Typography>
                                <Typography variant="body2" sx={{ color: "rgba(15, 23, 42, 0.65)", mt: 0.25 }}>
                                    Set SPOC contacts and threshold balance for tenant wallet.
                                </Typography>
                            </Box>
                            <Tooltip title="Close">
                                <IconButton
                                    onClick={handleCloseConfigModal}
                                    sx={{
                                        border: "1px solid rgba(15, 23, 42, 0.12)",
                                        transition: "transform 160ms ease, background-color 160ms ease",
                                        "&:hover": {
                                            transform: "rotate(90deg)",
                                            backgroundColor: "rgba(15, 23, 42, 0.06)",
                                        },
                                    }}
                                >
                                    <FiX />
                                </IconButton>
                            </Tooltip>
                        </Stack>

                        <Stack spacing={2.5}>
                            {selectedConfigTenant && (
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        bgcolor: "rgba(2, 132, 199, 0.06)",
                                        border: "1px solid rgba(2, 132, 199, 0.15)",
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: "rgba(15, 23, 42, 0.6)",
                                            textTransform: "uppercase",
                                            fontWeight: 700,
                                            letterSpacing: "0.05em",
                                        }}
                                    >
                                        Tenant
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 800, color: "rgb(2, 132, 199)", mt: 0.25 }}>
                                        {selectedConfigTenant.name}{" "}
                                        <Typography component="span" variant="body2" sx={{ color: "rgba(15, 23, 42, 0.6)", fontWeight: 500 }}>
                                            (ID: {selectedConfigTenant.tenant_id})
                                        </Typography>
                                    </Typography>
                                </Box>
                            )}

                            <TextField
                                label="Company SPOC Email"
                                placeholder="spoc@company.com"
                                fullWidth
                                size="small"
                                type="email"
                                value={companySpocEmail}
                                onChange={(e) => setCompanySpocEmail(e.target.value)}
                            />

                            <TextField
                                label="CSM Email"
                                placeholder="csm@company.com"
                                fullWidth
                                size="small"
                                type="email"
                                value={csmEmail}
                                onChange={(e) => setCsmEmail(e.target.value)}
                            />

                            <TextField
                                label="Accounts Team Email"
                                placeholder="accounts@company.com"
                                fullWidth
                                size="small"
                                type="email"
                                value={accountsEmail}
                                onChange={(e) => setAccountsEmail(e.target.value)}
                            />

                            <TextField
                                label="Threshold Balance (₹)"
                                placeholder="10000"
                                fullWidth
                                size="small"
                                type="number"
                                inputProps={{ min: 0, step: "1" }}
                                value={thresholdBalance}
                                onChange={(e) => setThresholdBalance(e.target.value)}
                            />

                            <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleCloseConfigModal}
                                    disabled={configSaving}
                                    sx={{ textTransform: "none", borderRadius: 2 }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleSaveConfig}
                                    disabled={configSaving || !selectedConfigTenant}
                                    startIcon={
                                        configSaving ? (
                                            <CircularProgress size={14} color="inherit" />
                                        ) : (
                                            <FiSave />
                                        )
                                    }
                                    sx={{
                                        textTransform: "none",
                                        borderRadius: 2,
                                        boxShadow: "none",
                                        background: "linear-gradient(135deg, #0284c7 0%, #2563eb 100%)",
                                    }}
                                >
                                    Save Details
                                </Button>
                            </Stack>
                        </Stack>
                    </Box>
                </Fade>
            </Modal>
        </div>
    );
};

export default TenantWallet;
